/**
 * Navigation helpers — Google Maps + Waze deep links.
 *
 * Universal URLs are used so both the native apps (iOS / Android) and the
 * mobile web fallback work from a single click without sniffing the user
 * agent. iOS Safari and Android Chrome auto-open the app when installed and
 * fall back to the web map otherwise — that's exactly what the spec asks for.
 *
 * Reusable across S.DRIVE rides today and personal driver rides tomorrow.
 */

/**
 * Decide which address the driver should be navigated to right now.
 *
 *   • Before pickup, en route, sur place → pickup address
 *   • Client onboard / déposé / completed → dropoff address
 *
 * `route_label` is the canonical "Pickup → Dropoff" string stored on every
 * ride. It may use either "→" or "->" as separator. We accept both.
 *
 * Falls back to explicit `pickup_location` / `dropoff_location` fields if the
 * ride object exposes them (e.g. personal rides). Returns `null` for any
 * field that is missing or empty so callers can disable buttons cleanly.
 */
export function pickNavigationTarget(ride) {
  if (!ride) return { pickup: null, dropoff: null, target: null, phase: null };

  let pickup = (ride.pickup_location || "").trim();
  let dropoff = (ride.dropoff_location || "").trim();

  // Phase 2D — Fallback structuré sur info_block (texte libre saisi par
  // l'agence ou parsé par l'IA). Ce bloc contient l'adresse complète vue
  // par le chauffeur (« Départ : Maison Albar Le Diamond, Paris »). On la
  // préfère au libellé court `route_label` qui n'est qu'un titre. Aligné
  // sur le pattern existant `extractFirstPhone(info_block)` côté driver.
  const ib = typeof ride.info_block === "string" ? ride.info_block : "";
  if (ib && (!pickup || !dropoff)) {
    const grab = (re) => {
      const m = ib.match(re);
      return m ? m[1].trim() : "";
    };
    if (!pickup) {
      pickup = grab(/(?:^|\n)\s*(?:Départ|Depart|Pickup|PEC|Prise en charge)\s*[:\-]\s*(.+)/i);
    }
    if (!dropoff) {
      dropoff = grab(/(?:^|\n)\s*(?:Arrivée|Arrivee|Dropoff|Drop[\s\-]?off|Destination)\s*[:\-]\s*(.+)/i);
    }
  }

  // S.DRIVE rides keep the route as a single "Pickup → Dropoff" string.
  if ((!pickup || !dropoff) && typeof ride.route_label === "string" && ride.route_label.length) {
    const sep = ride.route_label.includes("→") ? "→" : ride.route_label.includes("->") ? "->" : null;
    if (sep) {
      const [p, d] = ride.route_label.split(sep).map((s) => (s || "").trim());
      if (!pickup) pickup = p || "";
      if (!dropoff) dropoff = d || "";
    } else if (!pickup) {
      pickup = ride.route_label.trim();
    }
  }

  // Pre-pickup phases all navigate to the pickup address. PEC = Prise En
  // Charge (client onboard) flips the destination to the dropoff. Default
  // (e.g. "available", "interested", undefined) lands on the pickup too —
  // the driver may want to scout the address.
  const status = (ride.status || "").toLowerCase();
  let phase = "pickup";
  if (["pec", "dep", "completed"].includes(status)) phase = "dropoff";

  const target = phase === "dropoff" ? dropoff : pickup;
  return {
    pickup: pickup || null,
    dropoff: dropoff || null,
    target: target || null,
    phase,
  };
}

/**
 * Validate an address string before launching navigation. We don't need
 * geocoding accuracy — just enough sanity to avoid opening a maps URL with
 * "—" or "(none)" or a 1-character string.
 */
export function isUsableAddress(addr) {
  if (!addr || typeof addr !== "string") return false;
  const s = addr.trim();
  if (s.length < 3) return false;
  if (/^[—\-_.\s]+$/.test(s)) return false;
  return true;
}

/** Open Google Maps directions to an address (app on mobile, web elsewhere). */
export function openGoogleMaps(address) {
  if (!isUsableAddress(address)) return false;
  const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`;
  // `noopener` to not pass window.opener; fallback to assign() if popups blocked.
  const win = window.open(url, "_blank", "noopener");
  if (!win) window.location.href = url;
  return true;
}

/** Open Waze with navigation to an address (app on mobile, web elsewhere). */
export function openWaze(address) {
  if (!isUsableAddress(address)) return false;
  const url = `https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`;
  const win = window.open(url, "_blank", "noopener");
  if (!win) window.location.href = url;
  return true;
}
